import React, { useState } from 'react';
import { Button } from '../../../components/actions/Button';
import { ButtonColorsEnum } from '../../../constants/button-colors';
import { IconsEnum } from '../../../constants/icons';
import classes from './../Auth.module.scss';
import { Input } from '../../../components/forms/Input';
import { RegisterOptions, UseFormReturn } from 'react-hook-form';
import {
  LoginMutationFn,
  SocialOAuthContext,
  SocialOAuthProvider,
  StartSocialOAuthMutationFn
} from '../../../models/graphql';
import { BoxNotification } from '../../../components/containers/BoxNotification';
import { ApolloError } from '@apollo/client';
import { IInvitation } from '../../../models/auth';
import { Link } from 'react-router-dom';
import { InviteNotification } from '../InviteNotification';
import { showToast } from '../../../helpers/showToast';
import { remapApolloErrors } from '../../../helpers/remapApolloErrors';
import { InputTypes } from '../../../constants/form';
import { SubmitHandler } from 'react-hook-form/dist/types/form';
import { LoginFormVariables } from './Login.controller';

export type LoginViewProps = {
  //
  emailRules: RegisterOptions;
  formInstance: UseFormReturn<LoginFormVariables>;
  invitation: IInvitation | null;
  login: LoginMutationFn;
  loginError: string | JSX.Element;
  onSubmit: SubmitHandler<LoginFormVariables>;
  passwordRules: RegisterOptions;
  startSocialOAuth: StartSocialOAuthMutationFn;
  submitted: boolean;
};

export const LoginView = (props: LoginViewProps): JSX.Element => {
  const [selectedProvider, setSelectedProvider] = useState<SocialOAuthProvider | undefined>(undefined);
  const errorMessage = props.loginError ? (
    <div className={'margin-bottom-small-32'}>
      <BoxNotification type={'error'}>{props.loginError}</BoxNotification>
    </div>
  ) : null;
  const invitationBanner = props.invitation ? (
    <div className={'margin-bottom-small-32'}>
      <InviteNotification organizationName={props.invitation.organizationName} />
    </div>
  ) : null;
  // we need to cast UseFormReturn<T> back to UseFormReturn
  const formInstance: UseFormReturn = props.formInstance as unknown as UseFormReturn;
  return (
    <div className={classes.Title}>
      <div className={'margin-bottom-small-40'}>
        <h2>Log In</h2>
      </div>
      {invitationBanner}
      {errorMessage}
      <div className={'margin-bottom-small-16'}>
        <Button
          className={classes.GoogleButton}
          action={async () => {
            setSelectedProvider(SocialOAuthProvider.GOOGLE);
            try {
              await props.startSocialOAuth({
                variables: {
                  oAuthProvider: SocialOAuthProvider.GOOGLE,
                  context: SocialOAuthContext.LOGIN
                }
              });
            } catch (e) {
              /* we need to catch error to prevent runtime error */
              showToast({
                severity: 'error',
                summary: 'Authentication Error',
                detail: remapApolloErrors(e as ApolloError)
              });
            }
          }}
          color={ButtonColorsEnum.white}
          loading={props.submitted && selectedProvider === SocialOAuthProvider.GOOGLE}
          expanded
          disabled={props.submitted}
          icon={{ side: 'left', value: IconsEnum.google }}
          size={'large'}
          type={'button'}
          variant={'solid'}>
          Log In with Google
        </Button>
      </div>
      <div className={'margin-bottom-small-32'}>
        <Button
          action={async () => {
            setSelectedProvider(SocialOAuthProvider.FACEBOOK);
            try {
              await props.startSocialOAuth({
                variables: {
                  oAuthProvider: SocialOAuthProvider.FACEBOOK,
                  context: SocialOAuthContext.LOGIN
                }
              });
            } catch (e) {
              /* we need to catch error to prevent runtime error */
              showToast({
                severity: 'error',
                summary: 'Authentication Error',
                detail: remapApolloErrors(e as ApolloError)
              });
            }
          }}
          color={ButtonColorsEnum.facebook}
          loading={props.submitted && selectedProvider === SocialOAuthProvider.FACEBOOK}
          expanded
          disabled={props.submitted}
          icon={{ side: 'left', value: IconsEnum.facebook }}
          size={'large'}
          type={'button'}
          variant={'solid'}>
          Log In with Facebook
        </Button>
      </div>
      <div className={'grid-x align-justify'}>
        <div className={'cell shrink uppercase margin-bottom-small-32'}>
          <h6 className={'light-grey-text'}>or login with email</h6>
        </div>
        <div className={'show-for-tablet-portrait cell shrink uppercase margin-bottom-small-32'}>
          <h6>
            <Link to={'/forgot-password'}>forgot password</Link>
          </h6>
        </div>
      </div>
      <form className={'grid-x grid-padding-x'} onSubmit={props.formInstance.handleSubmit(props.onSubmit)}>
        <div className={'cell small-12 margin-bottom-small-26'}>
          <Input
            disabled={props.submitted}
            formInstance={formInstance}
            name={'email'}
            type={InputTypes.email}
            size={'large'}
            inlinedLabel
            label={'Email'}
            rules={props.emailRules}
          />
        </div>
        <div className={'cell small-12 margin-bottom-small-26'}>
          <Input
            disabled={props.submitted}
            formInstance={formInstance}
            name={'password'}
            type={InputTypes.password}
            size={'large'}
            inlinedLabel
            label={'Password'}
            rules={props.passwordRules}
          />
        </div>
        <div className={'cell small-12'}>
          <Button
            type={'submit'}
            variant={'solid'}
            size={'large'}
            expanded
            disabled={props.submitted}
            color={ButtonColorsEnum.secondary}
            loading={props.submitted && !selectedProvider}>
            Log In
          </Button>
        </div>
        <div className={'cell small-12 hide-for-tablet-portrait uppercase margin-top-small-32'}>
          <h6 className={'text-center'}>
            <Link to={'/forgot-password'}>forgot password</Link>
          </h6>
        </div>
      </form>
    </div>
  );
};
